import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { auth } from './firebase';
import {
  apiRequest,
  VIEW_AS_CLIENT_ID_KEY,
  VIEW_AS_CLIENT_NAME_KEY,
} from './api';
import type { Client, Me } from '@/types/auth';

interface AuthContextValue {
  me: Me | null;
  clients: Client[];
  loading: boolean;
  viewAsClientId: string | null;
  viewAsClientName: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [firebaseUser, setFirebaseUser] = useState<User | null>(auth.currentUser);
  const [firebaseResolved, setFirebaseResolved] = useState(!!auth.currentUser);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      if (!user) queryClient.clear();
      setFirebaseUser(user);
      setFirebaseResolved(true);
    });
  }, [queryClient]);

  const enabled = !!firebaseUser;

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiRequest<Me>('/auth/me'),
    enabled,
    staleTime: Infinity,
  });

  const clientsQuery = useQuery({
    queryKey: ['clients'],
    queryFn: () => apiRequest<Client[]>('/clients'),
    enabled,
  });

  const value: AuthContextValue = {
    me: meQuery.data ?? null,
    clients: clientsQuery.data ?? [],
    loading: !firebaseResolved || (enabled && (meQuery.isLoading || clientsQuery.isLoading)),
    viewAsClientId: localStorage.getItem(VIEW_AS_CLIENT_ID_KEY),
    viewAsClientName: localStorage.getItem(VIEW_AS_CLIENT_NAME_KEY),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

/** The active client's currency (clients.currency), defaulting to USD. */
export function useCurrency(): string {
  return useAuth().me?.currency ?? 'USD';
}

export function startViewAs(clientId: string, clientName: string) {
  localStorage.setItem(VIEW_AS_CLIENT_ID_KEY, clientId);
  localStorage.setItem(VIEW_AS_CLIENT_NAME_KEY, clientName);
  window.location.reload();
}

export function stopViewAs() {
  localStorage.removeItem(VIEW_AS_CLIENT_ID_KEY);
  localStorage.removeItem(VIEW_AS_CLIENT_NAME_KEY);
  window.location.reload();
}
